import { useCallback } from 'react';

import { useOutletContext, useRouteLoaderData } from '@remix-run/react';
import { useQuery } from '@tanstack/react-query';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';

const likedIdsKeys = {
  all: ['liked-ids'] as const,
  byPost: (postId: string) => ['liked-ids', postId] as const,
};

interface LikedIdsData {
  post_ids: string[];
  comment_ids: string[];
}

interface Props {
  postId?: string;
  enabled?: boolean;
}

const fetchLikedIds = async (postId?: string): Promise<LikedIdsData> => {
  const url = postId ? `/liked_ids?post_id=${postId}` : '/liked_ids';
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch liked IDs');
  return response.json();
};

const useLikedIds = ({ postId, enabled = true }: Props = {}) => {
  const { admin } = useOutletContext<OutletContext>();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const isLoggedIn = Boolean(admin) || Boolean(subscription);

  const { data, isLoading } = useQuery({
    queryKey: postId ? likedIdsKeys.byPost(postId) : likedIdsKeys.all,
    queryFn: () => fetchLikedIds(postId),
    enabled: enabled && isLoggedIn,
    staleTime: 5 * 60 * 1000, // 5 minutes - liked status doesn't change often
  });

  const likedPostIds = data?.post_ids ?? [];
  const likedCommentIds = data?.comment_ids ?? [];

  const isPostLiked = useCallback((id: string) => likedPostIds.includes(id), [likedPostIds]);
  const isCommentLiked = useCallback((id: string) => likedCommentIds.includes(id), [likedCommentIds]);

  return {
    isLoading,
    likedPostIds,
    likedCommentIds,
    isPostLiked,
    isCommentLiked,
  };
};

export default useLikedIds;
